import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Building2, Plus } from 'lucide-react';

/**
 * QuickAddEntrepriseButton — bouton + modal rapide pour créer un prospect/entreprise.
 * Props :
 *  - onCreated(prospect)  callback invoqué avec le prospect créé (côté API)
 *  - size, variant, label (optionnels)
 */
export default function QuickAddEntrepriseButton({ onCreated, label = 'Nouvelle entreprise', size = 'sm', variant = 'outline' }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ entreprise: '', contact: '', email: '', telephone: '', adresse: '', secteur: '' });

  const resetForm = () => setForm({ entreprise: '', contact: '', email: '', telephone: '', adresse: '', secteur: '' });

  const submit = async () => {
    if (!form.entreprise.trim()) { toast.error('Nom de l\'entreprise requis'); return; }
    setSaving(true);
    try {
      const payload = {
        entreprise: form.entreprise.trim(),
        nom_contact: form.contact.trim(),
        contact: form.contact.trim(),
        email: form.email.trim(),
        telephone: form.telephone.trim(),
        adresse: form.adresse.trim(),
        secteur: form.secteur.trim(),
        source: 'Conception',
        statut: 'Prospect',
      };
      const res = await axios.post('/prospects', payload);
      toast.success(`✓ ${payload.entreprise} créée`);
      resetForm();
      setOpen(false);
      onCreated?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création entreprise');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        size={size}
        variant={variant}
        onClick={() => setOpen(true)}
        className="gap-1 border-blue-200 text-blue-700 hover:bg-blue-50"
        data-testid="btn-nouvelle-entreprise"
      >
        <Plus className="h-3.5 w-3.5" /> {label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Building2 className="h-5 w-5 text-blue-600" />
              Nouvelle entreprise
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nom de l'entreprise <span className="text-rose-500">*</span></Label>
              <Input
                value={form.entreprise}
                onChange={(e) => setForm({ ...form, entreprise: e.target.value })}
                autoFocus
                data-testid="input-new-entreprise-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Contact</Label>
                <Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Secteur</Label>
                <Input value={form.secteur} onChange={(e) => setForm({ ...form, secteur: e.target.value })} placeholder="Ex: Commerce" />
              </div>
              <div>
                <Label className="text-xs">Téléphone</Label>
                <Input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Adresse</Label>
                <Input value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Annuler</Button>
              <Button onClick={submit} disabled={saving || !form.entreprise.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-save-new-entreprise">
                {saving ? 'Création…' : 'Créer et sélectionner'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
